import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import Image from 'next/image';
import VideoCard from '../components/VideoCard'; // 使用公共VideoCard组件
import { instagramTestimonials } from '../data/testimonials';

// Instagram 视频卡片数据类型（复用TikTok的类型）
export interface InstagramVideoData {
  id: string;
  username: string;
  userHandle: string;
  videoUrl: string;
  thumbnailUrl: string;
  link: string;
}

const InstagramSection: React.FC = () => {
  const { t } = useTranslation('common');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeVideo, setActiveVideo] = useState<{ id: string | null; requestKey: number }>({
    id: null,
    requestKey: 0,
  });

  // 从数据配置文件导入数据
  const instagramVideos: InstagramVideoData[] = instagramTestimonials;

  const handleVideoPlayRequest = useCallback((videoId: string) => {
    setActiveVideo((prev) => ({
      id: videoId,
      requestKey: prev.requestKey + 1,
    }));
  }, []);

  const handleVideoStopRequest = useCallback((videoId: string) => {
    setActiveVideo((prev) => {
      if (prev.id !== videoId) {
        return prev;
      }
      return {
        id: null,
        requestKey: prev.requestKey + 1,
      };
    });
  }, []);

  // 检查滚动位置
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // 滚动函数
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.6;
      const newScrollLeft =
        direction === 'left'
          ? scrollContainerRef.current.scrollLeft - scrollAmount
          : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });

      setTimeout(checkScrollPosition, 300);
    }
  };

  return (
    <div className="px-2 lg:px-0 py-[60px] pb-20 max-lg:py-10 max-lg:pb-[60px]  max-w-[1400px] mx-auto">
      {/* 标题和导航箭头 */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3 max-md:gap-2">
          <Image
            src="/images/testimonials/instagramtag.svg"
            alt="Instagram"
            width={28}
            height={28}
            className="object-contain max-md:w-6 max-md:h-6"
          />
          <h2 className="text-[32px] max-lg:text-[28px] max-md:text-2xl font-bold text-[#0c0c0c] m-0">Instagram</h2>
        </div>
        <div className="flex gap-3 max-md:gap-2">
          <button
            className={`size-8 rounded-lg border border-[#e0e0e0] bg-white text-[#333333] cursor-pointer transition-all duration-300 flex items-center justify-center text-base max-md:text-sm ${
              !canScrollLeft
                ? 'opacity-30 cursor-not-allowed'
                : 'hover:border-[var(--app-primary-color,#725CFF)] hover:text-[var(--app-primary-color,#725CFF)] hover:shadow-[0_2px_8px_rgba(114,92,255,0.2)]'
            }`}
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
          >
            <LeftOutlined />
          </button>
          <button
            className={`size-8 rounded-lg border border-[#e0e0e0] bg-white text-[#333333] cursor-pointer transition-all duration-300 flex items-center justify-center text-base max-md:text-sm ${
              !canScrollRight
                ? 'opacity-30 cursor-not-allowed'
                : 'hover:border-[var(--app-primary-color,#725CFF)] hover:text-[var(--app-primary-color,#725CFF)] hover:shadow-[0_2px_8px_rgba(114,92,255,0.2)]'
            }`}
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
          >
            <RightOutlined />
          </button>
        </div>
      </div>

      {/* 视频卡片容器 */}
      <div
        className="flex gap-5 max-lg:gap-4 max-md:gap-3 overflow-x-auto pb-5 scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        ref={scrollContainerRef}
        onScroll={checkScrollPosition}
      >
        {instagramVideos.map((video) => (
          <VideoCard key={video.id} data={video} platform="instagram" />
        ))}
      </div>
    </div>
  );
};

export default InstagramSection;
